import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastService } from '@y42/toast';
import { filter, merge, Subject, switchMap, tap } from 'rxjs';
import { ImageUploadService } from '../upload/image-upload.service';
import { ImageCropDialogService } from './image-crop-dialog.service';

export const MAX_FILE_SIZE = 500000; // In bytes, ~500 KB

@UntilDestroy()
@Component({
  selector: 'y42-image-crop',
  templateUrl: './image-crop.component.html',
  styleUrls: ['./image-crop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ImageCropDialogService, ImageUploadService],
})
export class ImageCropComponent implements OnInit {
  constructor(
    private readonly imageCropService: ImageCropDialogService,
    private readonly imageUploadService: ImageUploadService,
    private readonly toastService: ToastService,
  ) {}

  @Input() imageFormCtrl!: FormControl<string>;
  @ViewChild('imageUploadInput') imageUploadInput!: ElementRef;

  private readonly uploadFile$ = new Subject<File>();

  ngOnInit(): void {
    this.handleImageUpload();
  }

  onImageSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (!files || !files.length || files.length > 1) {
      return;
    }

    const file = files.item(0);
    if (file?.type.match('svg')) {
      this.uploadFile$.next(file);
    } else {
      this.imageCropService.showCropper(event);
    }
  }

  trigger() {
    this.imageUploadInput.nativeElement.click();
  }

  private handleImageUpload() {
    merge(this.uploadFile$, this.imageCropService.onCrop())
      .pipe(
        filter((file) => {
          if (file.size > MAX_FILE_SIZE) {
            this.toastService.error(`Image size should not exceed ${MAX_FILE_SIZE / 1000} KB`);
            return false;
          }
          return true;
        }),
        switchMap((fileToUpload) => {
          const formData = this.getImageFormData(fileToUpload);
          return this.imageUploadService.uploadImage(formData);
        }),
        tap((img) => this.imageFormCtrl.setValue(img.url)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private getImageFormData(fileToUpload: File | Blob) {
    const name = fileToUpload instanceof File ? fileToUpload.name : 'upload.png';
    const formData: FormData = new FormData();
    formData.append('media', fileToUpload, name);
    return formData;
  }
}
